import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AiOutlineClose } from 'react-icons/ai';
import { cx, useOverlay } from './hooks';

/**
 * Dialog rendered in a portal. Content is unmounted while closed.
 *
 * footer: pass your own buttons, or null for none. When footer is left out
 * and onOk is given, a default Cancel / OK footer is rendered.
 */
const Modal = ({
  open,
  onClose,
  title,
  children,
  footer,
  width,
  centered = false,
  closable = true,
  maskClosable = true,
  className,
  bodyClassName,
  onOk,
  okText = 'OK',
  cancelText = 'Cancel',
  confirmLoading = false,
  okDanger = false,
  afterClose,
}) => {
  const panelRef = useRef(null);
  const pressStartedOnBackdrop = useRef(false);
  const wasOpen = useRef(open);
  const titleId = useId();

  useOverlay(open, onClose, panelRef);

  useEffect(() => {
    if (wasOpen.current && !open) afterClose?.();
    wasOpen.current = open;
  }, [open, afterClose]);

  if (!open) return null;

  const defaultFooter =
    footer === undefined && onOk ? (
      <>
        <button type="button" className="btn" onClick={onClose}>
          {cancelText}
        </button>
        <button
          type="button"
          className={cx('btn btn-primary', okDanger && 'btn-danger')}
          onClick={onOk}
          disabled={confirmLoading}
          aria-busy={confirmLoading || undefined}
        >
          {okText}
        </button>
      </>
    ) : null;
  const footerContent = footer === undefined ? defaultFooter : footer;

  const panelStyle =
    width !== undefined ? { '--modal-w': typeof width === 'number' ? `${width}px` : width } : undefined;

  return createPortal(
    <div
      className={cx('modal-backdrop', centered && 'is-centered')}
      onMouseDown={e => {
        pressStartedOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={e => {
        if (maskClosable && pressStartedOnBackdrop.current && e.target === e.currentTarget) {
          onClose?.();
        }
      }}
    >
      <div
        ref={panelRef}
        className={cx('modal', className)}
        style={panelStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
      >
        {closable && (
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <AiOutlineClose />
          </button>
        )}
        {title && (
          <div className="modal-head">
            <div className="modal-title" id={titleId}>
              {title}
            </div>
          </div>
        )}
        <div className={cx('modal-body', bodyClassName)}>{children}</div>
        {footerContent && <div className="modal-foot">{footerContent}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
